import { useQuery, type UseQueryOptions } from 'react-query';
import { getSubscriptionStatus, getBusinessDetails, getActiveBusinessPlan, getBusinessPlans } from '../api';
import { TRUELARK_STALE_TIME } from '../constants';
import type {
  TrueLarkBusinessDetails,
  TrueLarkSubscriptionResponse,
  BusinessPlanDetails,
  TrueLarkDomains,
  TrueLarkPlansResponse,
} from '../types';

export const queryKeys = {
  subscriptionStatus: (locationId: string) => ['truelark-subscription', locationId] as const,
  businessDetails: (locationId: string) => ['truelark-business-details', locationId] as const,
  businessPlanDetails: (locationId: string) => ['truelark-active-business-plan', locationId] as const,
  businessPlans: (domain: TrueLarkDomains) => ['truelark-business-plans', domain] as const,
};

export const useSubscriptionStatus = (
  locationId: string | undefined,
  options: UseQueryOptions<TrueLarkSubscriptionResponse> = {}
) =>
  useQuery({
    queryKey: queryKeys.subscriptionStatus(locationId ?? ''),
    queryFn: () => getSubscriptionStatus(locationId!),
    enabled: !!locationId,
    staleTime: TRUELARK_STALE_TIME,
    ...options,
  });

export const useBusinessDetails = (
  locationId: string | undefined,
  options: UseQueryOptions<TrueLarkBusinessDetails> = {}
) =>
  useQuery({
    queryKey: queryKeys.businessDetails(locationId ?? ''),
    queryFn: () => getBusinessDetails(locationId!),
    enabled: !!locationId,
    staleTime: TRUELARK_STALE_TIME,
    ...options,
  });

export const useActiveBusinessPlan = (
  locationId: string | undefined,
  options: UseQueryOptions<BusinessPlanDetails> = {}
) =>
  useQuery({
    queryKey: queryKeys.businessPlanDetails(locationId ?? ''),
    queryFn: () => getActiveBusinessPlan(locationId!),
    enabled: !!locationId,
    staleTime: TRUELARK_STALE_TIME,
    ...options,
  });

export const useBusinessPlans = (
  domain: TrueLarkDomains | undefined,
  options: UseQueryOptions<TrueLarkPlansResponse> = {}
) =>
  useQuery({
    queryKey: queryKeys.businessPlans(domain ?? ('' as TrueLarkDomains)),
    queryFn: () => getBusinessPlans(domain!),
    enabled: !!domain,
    staleTime: TRUELARK_STALE_TIME,
    ...options,
  });
